import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { SupplierDocument } from './types';

const getEndpoint = (supplierProfileId: number) => `ums/v1/cms/supplier-profiles/${supplierProfileId}/documents`;

export const supplierDocumentsQueryKey = 'supplier-documents';

type TData = SupplierDocument[];
type TError = AxiosError;
type TQueryKey = [typeof supplierDocumentsQueryKey, number | null];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/** Newest first. `supplierProfileId: null` keeps the query dormant so the modal only
 * fetches once it is opened — the URLs are presigned (~15 min) and there is no point
 * minting them for every row of the list. */
export function useGetSupplierDocumentsApi(supplierProfileId: number | null, options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(getEndpoint(supplierProfileId as number));
      return data?.data ?? data ?? [];
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([supplierDocumentsQueryKey, supplierProfileId], queryFn, {
    enabled: isLoggedIn && supplierProfileId !== null,
    ...options,
  });
}
